import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronUp,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';


function InteriorDesignFAQ() {
  const faqData = [
    // 1. FAQs taken directly from the image
    {
      question: "What are the services provided by interior designers in Madipakkam, Chennai?",
      answer: "Interior designers provide many services like interior decor, colour selection, furniture selection, lighting designs, flooring selection, product and cost comparisons, floor planning, wall and window treatments, product research, and renovation, etc.",
    },
    {
      question: "Do interior designers near me offer services for a small living space?",
      answer: "Interior decorators or designers have knowledge on how to use furnishings, colours, and art to make even a small space look larger. They are experts in designing any space whether it is small or spacious.",
    },
    {
      question: "Do interior designers provide sample work?",
      answer: "Yes, most interior designers archive their previous projects so that their clients can refer to them and understand the quality of work they can expect. (You can view our portfolio in the Gallery section above!)",
    },
    {
      question: "How long does interior designers in Madipakkam, Chennai take to design the home?",
      answer: "Generally, interior designs for home take three months for smaller projects. However, the larger projects can take up to one year. However, you can always contact and ask a professional interior designer about duration.",
    },
    {
      question: "What type of spaces do interior designers design?",
      answer: "Interior designers design spaces like apartments, offices, restaurants, old and new constructions, etc. Many designers are also known to provide interior design services for salons, lavish mansions, resorts, corporate buildings, and so on.",
    },
    // 2. Additional, related FAQs for a comprehensive section
    {
      question: "How do you handle budgeting and cost control during a project?",
      answer: "Budget management is critical. We provide a detailed cost breakdown before starting any work. We use transparent pricing for all materials and labor, and proactively communicate any potential cost deviations before they happen, ensuring you stay in full control.",
    },
    {
      question: "Can I use my existing furniture in the new design plan?",
      answer: "Absolutely! We specialize in integrating treasured pieces. We'll assess the condition, style, and scale of your existing furniture and creatively incorporate it into the new design, saving costs while maintaining a personal touch.",
    },
    {
      question: "What is your approach to modern, high contrast (Red, Black, White) design?",
      answer: "Our signature style focuses on bold minimalism. We use black and white as the foundational canvas to maximize spatial depth and light, then apply strategic pops of red (or other accent colors) to create focal points, energy, and a sophisticated, dramatic atmosphere.",
    },
    {
      question: "What is the typical design process, from consultation to completion?",
      answer: "Our process includes five key phases: 1. Consultation & Concept: Understanding your needs and defining the style. 2. Design & Planning: Creating 3D renderings, floor plans, and material boards. 3. Material Sourcing & Procurement: Ordering and managing the delivery of all elements. 4. Execution & Build: On-site management of contractors and installation. 5. Final Styling & Handover: The reveal of your finished space.",
    },
    {
      question: "Do you manage the contractors and construction work?",
      answer: "Yes. For our full-service clients, we handle all aspects of project execution. This includes selecting, hiring, and managing all contractors, tradespeople, and vendors to ensure the design is executed precisely to plan, saving you time and stress.",
    },
  ];

  const [openIndex, setOpenIndex] = useState(null);
  const faqRefs = useRef([]); // Ref to hold references to each FAQ item for IntersectionObserver

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Re-implemented Scroll Animation Logic (Staggered Fade-in)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible'); // Trigger the animation
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    // Observe each FAQ item for the staggered effect
    faqRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    
    // Also observe the section title for entry animation
    document.querySelectorAll('.section-title-animate').forEach((el) => {
      observer.observe(el);
    });

    return () => {
      faqRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
      document.querySelectorAll('.section-title-animate').forEach((el) => {
        observer.unobserve(el);
      });
    };
  }, []);


  return (
    <>
      <style jsx>{`
        /* Define the initial hidden state for staggered scroll animation */
        .faq-item-animate {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.7s ease-out, transform 0.7s ease-out, box-shadow 0.3s ease, border-color 0.3s ease; /* Ensure transitions include hover effects */
        }

        /* Define the final visible state and stagger */
        .faq-item-animate.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Staggered animation delays */
        .faq-item-animate:nth-child(1) { transition-delay: 0.1s; }
        .faq-item-animate:nth-child(2) { transition-delay: 0.2s; }
        .faq-item-animate:nth-child(3) { transition-delay: 0.3s; }
        .faq-item-animate:nth-child(4) { transition-delay: 0.4s; }
        .faq-item-animate:nth-child(5) { transition-delay: 0.5s; }
        .faq-item-animate:nth-child(6) { transition-delay: 0.6s; }
        .faq-item-animate:nth-child(7) { transition-delay: 0.7s; }
        .faq-item-animate:nth-child(8) { transition-delay: 0.8s; }
        .faq-item-animate:nth-child(9) { transition-delay: 0.9s; }
        .faq-item-animate:nth-child(10) { transition-delay: 1.0s; }


        /* Color Scheme Overrides and Base Styles */
        .faq-accent-text {
          color: #ff0000; /* Red */
        }
        .faq-text {
          color: #333; /* Dark Gray/Black */
          transition: color 0.3s ease; /* Added for hover color transition */
        }
        .faq-border {
          border: 1px solid #333; /* Strong black border */
          transition: border-color 0.3s ease, transform 0.3s ease;
        }
        .faq-item-base {
            background-color: #fff;
            position: relative; /* Needed for hover transformation */
            z-index: 10;
        }
        .faq-shadow {
             box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06); 
             transition: box-shadow 0.3s ease;
        }

        /* 🌟 NEW: FAQ Container Hover Animation 🌟 */
        .faq-item-base:hover {
            transform: translateY(-3px); /* Lift up slightly */
            box-shadow: 0 10px 15px -3px rgba(255, 0, 0, 0.1), 0 4px 6px -4px rgba(255, 0, 0, 0.1); /* Red glow shadow */
            border-color: #ff0000; /* Red border on hover */
        }

        /* 🌟 NEW: Toggle Button Hover Animation 🌟 */
        .faq-toggle:hover .faq-text {
            color: #ff0000; /* Question text turns red on hover */
        }
        .faq-toggle:hover {
            background-color: #fef0f0; /* Light Red background hover */
        }
      `}</style>
      <section className="py-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="faq-accent-text font-medium section-title-animate faq-item-animate">ANSWERS</span>
            <h2 className="text-3xl md:text-4xl font-bold faq-text mt-2 mb-4 section-title-animate faq-item-animate">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto section-title-animate faq-item-animate">Find immediate answers about our services, process, and signature high contrast design philosophy.</p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  ref={(el) => (faqRefs.current[index] = el)} // Attach ref for scroll animation
                  // Use combined class names for base styling and animation
                  className="faq-item-base faq-border border rounded-xl overflow-hidden faq-shadow faq-item-animate"
                >
                  <button
                    className="faq-toggle w-full flex justify-between items-center p-6 text-left transition-colors duration-300"
                    onClick={() => handleToggle(index)}
                  >
                    <h3 className="text-lg font-medium faq-text">{faq.question}</h3>
                    <FontAwesomeIcon
                      icon={openIndex === index ? faChevronUp : faChevronDown}
                      className="faq-accent-text transition-transform duration-300"
                    />
                  </button>
                  {/* Smooth accordion content */}
                  <div
                    className={`faq-content transition-all duration-500 ease-in-out ${
                      openIndex === index ? 'max-h-screen opacity-100 py-4' : 'max-h-0 opacity-0'
                    } overflow-hidden px-6`}
                  >
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default InteriorDesignFAQ;
